import React, { useState, useRef, useEffect } from "react";
import {
  FaChevronUp,
  FaChevronDown,
  FaTimes,
  FaCheck,
  FaBox,
  FaTrafficLight,
  FaTruck,
  FaShippingFast,
} from "react-icons/fa";

function _OrderRow({
  order,
  menuOpenId,
  setMenuOpenId,
  isSelected,
  onSelect,
  expandedId,
  setExpandedId,
  handleUpdateStatus,
}) {
  const [transactions, setTransactions] = useState({}); // ✅ state to store history per order

  const [defaultAddress, setDefaultAddress] = useState({});
  const [activeTab, setActiveTab] = useState("address");
  const [height, setHeight] = useState("0px");
  const contentRef = useRef(null);
  const isExpanded = expandedId === order.COI_ID;

  const formatToMMDDYYYY = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);

    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();

    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12; // Convert to 12-hour format

    return `${mm}/${dd}/${yyyy} ${hours}:${minutes} ${ampm}`;
  };
  useEffect(() => {
    if (contentRef.current) {
      setHeight(isExpanded ? `${contentRef.current.scrollHeight}px` : "0px");
    }
  }, [isExpanded, transactions, activeTab]);

  // ✅ Automatically load transaction history when expanded
  useEffect(() => {
    const loadTransactionHistory = async () => {
      if (isExpanded && !transactions[order.COI_ID]) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}api/orders/get-transaction-history/${order.COI_ID}`
          );
          const data = await res.json();
          setTransactions((prev) => ({
            ...prev,
            [order.COI_ID]: data.transactions,
          }));
        } catch (error) {
          console.error("Failed to load transaction history:", error);
        }
      }
    };

    const loadDefaultAddress = async () => {
      if (isExpanded && !defaultAddress[order.CP_ID]) {
        try {
          const res = await fetch(
            `${process.env.REACT_APP_BASE_URL}api/orders/get-default-address-by-CA_ID/${order.CA_ID}`
          );
          const data = await res.json();
          setDefaultAddress((prev) => ({
            ...prev,
            [order.CP_ID]: data.addressRow?.[0] || {}, // ✅ Get first row
          }));
        } catch (error) {
          console.error("Failed to load default address:", error);
        }
      }
    };

    loadDefaultAddress();
    loadTransactionHistory();
  }, [isExpanded, order.COI_ID, order.CP_ID, transactions, defaultAddress]);

  return (
    <>
      <tr>
        {/* Select Checkbox */} {/* Select Checkbox */}{" "}
        {/* Expand / Collapse Icon */}
        <td
          className="td-toggle-icon"
          onClick={() => setExpandedId(isExpanded ? null : order.COI_ID)}
          style={{ cursor: "pointer", width: "40px" }}
        >
          {isExpanded ? (
            <FaChevronUp className="toggle-icon" title="Collapse" />
          ) : (
            <FaChevronDown className="toggle-icon" title="Expand" />
          )}
        </td>
        <td>
          <input type="checkbox" checked={isSelected} onChange={onSelect} />
        </td>
        <td>
          <img
            src={`${process.env.REACT_APP_BASE_URL}Assets/Products/${
              order.PD_ID
            }/${order.PD_ProductPicPath[0] ?? "no-image.png"}`}
            alt={order.PD_Name}
            className="ord-prod-pic"
          />
        </td>
        <td>{order.PD_Name}</td>
        <td>{order.CP_Firstname + " " + order.CP_Lastname}</td>
        <td>{order.PD_ProductType}</td>
        <td>{formatToMMDDYYYY(order.COI_Created_At)}</td>
        <td>PHP {order.COI_UnitPrice}</td>
        <td> {order.COI_Quantity}</td>
        <td>PHP {order.COI_Downpayment}</td>
        <td>PHP {order.COI_Balance}</td>
        <td>
          {order.COI_Status === "Paid" ? "FP Paid" : order.COI_Status || ""}
        </td>
        <td className="td-actions">
          <div className="td-action-btns-wrapper">
            <FaShippingFast
              className="readytoship"
              title="DISPATCHED"
              style={{ color: "green", cursor: "pointer" }}
              onClick={() => handleUpdateStatus(order)}
            />
          </div>
        </td>
      </tr>

      {/* Expandable Section */}
      <tr>
        <td
          className="td-other-order-field"
          colSpan={15}
          style={{ padding: 0, border: 0 }}
        >
          <div
            ref={contentRef}
            style={{
              maxHeight: height,
              overflow: "hidden",
              transition: "max-height 0.4s ease, opacity 0.3s ease",
              opacity: isExpanded ? 1 : 0,
            }}
          >
            {/* Tabs Header */}
            <div
              className="expand-links"
              style={{
                padding: "10px 20px",
                borderBottom: "1px solid #ddd",
                display: "flex",
                gap: "15px",
              }}
            >
              <span
                onClick={() => setActiveTab("address")}
                style={{
                  cursor: "pointer",
                  color: activeTab === "address" ? "#007bff" : "#555",
                  textDecoration:
                    activeTab === "address" ? "underline" : "none",
                  fontWeight: activeTab === "address" ? "600" : "400",
                }}
              >
                DELIVERY ADDRESS
              </span>
              <span
                onClick={() => setActiveTab("history")}
                style={{
                  cursor: "pointer",
                  color: activeTab === "history" ? "#007bff" : "#555",
                  textDecoration:
                    activeTab === "history" ? "underline" : "none",
                  fontWeight: activeTab === "history" ? "600" : "400",
                }}
              >
                View History
              </span>
            </div>

            {/* Tab Content */}
            <div className="tab-content" style={{ padding: "10px 25px" }}>
              {/* ✅ ADDRESS TAB */}
              {activeTab === "address" && (
                <div className="order-address">
                  <h4>Delivery Address</h4>
                  {defaultAddress[order.CP_ID] ? (
                    <>
                      <p>
                        <strong>Recipient:</strong>{" "}
                        {defaultAddress[order.CP_ID].CA_Fullname || "N/A"}
                      </p>
                      <p>
                        <strong>Contact Number:</strong>{" "}
                        {defaultAddress[order.CP_ID].CA_Contactnumber || "N/A"}
                      </p>
                      <p>
                        <strong>Address:</strong>{" "}
                        {[
                          defaultAddress[order.CP_ID].CA_StreetAddress,
                          defaultAddress[order.CP_ID].CA_Barangay,
                          defaultAddress[order.CP_ID].CA_CityMunicipality,
                          defaultAddress[order.CP_ID].CA_DistrictProvince,
                          defaultAddress[order.CP_ID].CA_Region,
                        ]
                          .filter(Boolean)
                          .join(", ") || "No delivery address provided."}
                      </p>
                      <p>
                        <strong>Postal Code:</strong>{" "}
                        {defaultAddress[order.CP_ID].CA_PostalCode || "N/A"}
                      </p>
                      {defaultAddress[order.CP_ID].CA_Landmark && (
                        <p>
                          <strong>Landmark:</strong>{" "}
                          {defaultAddress[order.CP_ID].CA_Landmark}
                        </p>
                      )}
                    </>
                  ) : (
                    <p>Loading address...</p>
                  )}
                </div>
              )}

              {/* ✅ HISTORY TAB */}
              {activeTab === "history" && (
                <div className="order-history">
                  <h4> Transaction History</h4>

                  {transactions[order.COI_ID] ? (
                    transactions[order.COI_ID].length > 0 ? (
                      transactions[order.COI_ID].map((tx, idx) => (
                        <div key={idx} style={{ marginBottom: "10px" }}>
                          <p>
                            <strong>{formatToMMDDYYYY(tx.DatePurchase)}</strong>{" "}
                            – {tx.PaymentType} via {tx.PaymentMethod} – PHP{" "}
                            {tx.PaymentAmount}
                            <br />
                            Status: {tx.Status} <br />
                            <a
                              href={`${process.env.REACT_APP_BASE_URL}Assets/purchase_order/${tx.TransactionID}/${tx.Receipt}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "blue",
                                cursor: "pointer",
                              }}
                            >
                              <span>VIEW RECEIPT</span>
                            </a>
                          </p>

                          <ul>
                            {tx.OrderItems.map((item, i) => {
                              // ✅ Highlight the item if it matches the current order product
                              const isHighlighted =
                                item.ProductID === order.PD_ID ||
                                item.ProductName === order.PD_Name;

                              return (
                                <li
                                  key={i}
                                  style={{
                                    backgroundColor: isHighlighted
                                      ? "gray"
                                      : "transparent", // light blue
                                    borderRadius: "5px",
                                    padding: "4px 8px",
                                    fontWeight: isHighlighted
                                      ? "600"
                                      : "normal",
                                    transition: "background-color 0.3s ease",
                                  }}
                                >
                                  {item.ProductName} × {item.Quantity} @ PHP{" "}
                                  {item.UnitPrice} — Deposited: PHP{" "}
                                  {item.Deposited} Balance: PHP {item.Balance}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))
                    ) : (
                      <p>No transactions available</p>
                    )
                  ) : (
                    <p>Loading transaction history...</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

export default _OrderRow;
